"use client";

import { cn } from "@jesus-central-church/shared";
import { motion, useInView, useScroll, useTransform } from "motion/react";
import Image from "next/image";
import { useRef } from "react";

export interface UiKidsGroup {
  name: string;
  ageRange: string;
  icon: string;
  description?: string;
  color?: string;
}

export interface KidsGroupsProps {
  groups: UiKidsGroup[];
  className?: string;
  variant?: "grid" | "carousel" | "stacked";
}

// Vibrant color palette for kids - warm and playful matching site aesthetic
const GROUP_COLORS = [
  { bg: "bg-[#c89657]", accent: "#c89657", name: "gold" },
  { bg: "bg-[#7a8c4e]", accent: "#7a8c4e", name: "olive" },
  { bg: "bg-[#e85d3b]", accent: "#e85d3b", name: "coral" },
  { bg: "bg-[#5a8fa8]", accent: "#5a8fa8", name: "teal" },
  { bg: "bg-[#9b6b9e]", accent: "#9b6b9e", name: "lavender" },
  { bg: "bg-[#d4a853]", accent: "#d4a853", name: "amber" },
  { bg: "bg-[#6b9b7a]", accent: "#6b9b7a", name: "sage" },
  { bg: "bg-[#c97b63]", accent: "#c97b63", name: "terracotta" },
  { bg: "bg-[#8fa5b5]", accent: "#8fa5b5", name: "slate" },
  { bg: "bg-[#b8936e]", accent: "#b8936e", name: "caramel" },
  { bg: "bg-[#7a9e8e]", accent: "#7a9e8e", name: "mint" },
  { bg: "bg-[#cf8a5b]", accent: "#cf8a5b", name: "peach" },
];

export function KidsGroups({
  groups,
  className,
  variant = "carousel",
}: KidsGroupsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  if (variant === "stacked") {
    return (
      <StackedKidsGroups
        groups={groups}
        className={className}
        isInView={isInView}
        containerRef={containerRef}
      />
    );
  }

  if (variant === "grid") {
    return (
      <GridKidsGroups
        groups={groups}
        className={className}
        isInView={isInView}
        containerRef={containerRef}
      />
    );
  }

  return (
    <CarouselKidsGroups
      groups={groups}
      className={className}
      isInView={isInView}
      containerRef={containerRef}
    />
  );
}

// Carousel variant - horizontal scroll with snap
function CarouselKidsGroups({
  groups,
  className,
  isInView,
  containerRef,
}: {
  groups: UiKidsGroup[];
  className?: string;
  isInView: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div ref={containerRef} className={cn("relative w-full py-6", className)}>
      {/* Scroll hint gradient */}
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-32 bg-gradient-to-l from-black/20 to-transparent" />

      <motion.div
        className="flex gap-5 overflow-x-auto py-4 pl-4 px-1 snap-x snap-mandatory scrollbar-hide"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {groups.map((group, index) => (
          <motion.div
            key={group.name}
            initial={{ opacity: 0, x: 60, scale: 0.9 }}
            animate={
              isInView
                ? { opacity: 1, x: 0, scale: 1 }
                : { opacity: 0, x: 60, scale: 0.9 }
            }
            transition={{
              duration: 0.6,
              delay: index * 0.08,
              ease: [0.21, 1.02, 0.73, 1],
            }}
            className="snap-center"
          >
            <KidsGroupCard group={group} index={index} />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

// Grid variant - responsive grid layout
function GridKidsGroups({
  groups,
  className,
  isInView,
  containerRef,
}: {
  groups: UiKidsGroup[];
  className?: string;
  isInView: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={containerRef}
      className={cn(
        "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5",
        className,
      )}
    >
      {groups.map((group, index) => (
        <motion.div
          key={group.name}
          initial={{ opacity: 0, y: 40, scale: 0.9 }}
          animate={
            isInView
              ? { opacity: 1, y: 0, scale: 1 }
              : { opacity: 0, y: 40, scale: 0.9 }
          }
          transition={{
            duration: 0.5,
            delay: index * 0.06,
            ease: [0.21, 1.02, 0.73, 1],
          }}
        >
          <KidsGroupCard group={group} index={index} />
        </motion.div>
      ))}
    </div>
  );
}

// Stacked variant - overlapping cards with parallax
function StackedKidsGroups({
  groups,
  className,
  isInView,
  containerRef,
}: {
  groups: UiKidsGroup[];
  className?: string;
  isInView: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  return (
    <div
      ref={containerRef}
      className={cn("relative min-h-[600px] py-12", className)}
    >
      <div className="sticky top-24 flex flex-wrap justify-center gap-4 px-4">
        {groups.slice(0, 6).map((group, index) => {
          const rotation = useTransform(
            scrollYProgress,
            [0, 1],
            [index % 2 === 0 ? -5 : 5, index % 2 === 0 ? 5 : -5],
          );
          const y = useTransform(
            scrollYProgress,
            [0, 1],
            [0, (index % 3) * 20 - 20],
          );

          return (
            <motion.div
              key={group.name}
              style={{ rotate: rotation, y }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={
                isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
              }
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.21, 1.02, 0.73, 1],
              }}
            >
              <KidsGroupCard group={group} index={index} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Main card component with modern design
function KidsGroupCard({
  group,
  index,
}: {
  group: UiKidsGroup;
  index: number;
}) {
  const colorScheme = GROUP_COLORS[index % GROUP_COLORS.length];

  return (
    <motion.div
      className="group relative"
      whileHover={{ y: -8, scale: 1.01 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
    >
      <div
        className={cn(
          "relative w-[240px] h-[320px] rounded-3xl overflow-hidden cursor-pointer",
          "shadow-xl transition-shadow duration-500",
          "group-hover:shadow-2xl group-hover:shadow-black/30",
          colorScheme.bg,
        )}
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-20">
          <motion.div
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/30"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 10, 0],
              y: [0, -10, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-black/10"
            animate={{
              scale: [1, 1.15, 1],
              x: [0, -5, 0],
              y: [0, 5, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5,
            }}
          />
        </div>

        {/* Icon container with floating animation */}
        <motion.div
          className="relative z-10 flex justify-center pt-8"
          animate={{
            y: [0, -6, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: index * 0.2,
          }}
        >
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full bg-white/20 blur-xl scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Icon wrapper */}
            <motion.div
              className="relative w-28 h-28 rounded-full bg-white/95 shadow-lg flex items-center justify-center"
              whileHover={{
                rotate: [0, -5, 5, -3, 0],
                transition: { duration: 0.5 },
              }}
            >
              <Image
                src={group.icon}
                alt={group.name}
                width={80}
                height={80}
                className="object-contain drop-shadow-md"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Content */}
        <div className="relative z-10 px-5 pt-6 text-center">
          <h3 className="font-heading text-2xl font-bold text-white uppercase tracking-wide drop-shadow-md">
            {group.name}
          </h3>

          {/* Age badge */}
          <motion.div
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full"
            whileHover={{
              scale: 1.05,
              backgroundColor: "rgba(255,255,255,0.3)",
            }}
          >
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span className="text-sm font-semibold text-white">
              {group.ageRange}
            </span>
          </motion.div>

          {/* Description */}
          {group.description && (
            <p className="mt-4 text-sm text-white/80 leading-relaxed line-clamp-2">
              {group.description}
            </p>
          )}
        </div>

        {/* Bottom decorative wave */}
        <div className="absolute bottom-0 left-0 right-0 h-16">
          <svg
            viewBox="0 0 240 64"
            fill="none"
            className="absolute bottom-0 w-full h-16 text-white/10"
            preserveAspectRatio="none"
          >
            <motion.path
              d="M0 32C40 32 40 48 80 48C120 48 120 16 160 16C200 16 200 32 240 32V64H0V32Z"
              fill="currentColor"
              animate={{
                d: [
                  "M0 32C40 32 40 48 80 48C120 48 120 16 160 16C200 16 200 32 240 32V64H0V32Z",
                  "M0 40C40 40 40 24 80 24C120 24 120 48 160 48C200 48 200 40 240 40V64H0V32Z",
                  "M0 32C40 32 40 48 80 48C120 48 120 16 160 16C200 16 200 32 240 32V64H0V32Z",
                ],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </svg>
        </div>

        {/* Hover shine effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/0 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          initial={false}
        />
      </div>
    </motion.div>
  );
}
